// PARTNER-PORTÁL · Szolgáltatások és díjak (`/portal/invoices` — az URL a mély-linkek miatt maradt) — három
// nézet egy fül-sávval (sorrend: Havi áttekintés · Szolgáltatások · Számlák). Az egész lap a `billingView` jog mögött áll:
//  • Havi áttekintés (alap): hónaponként a TÉNY (számlák tételesen) és a TERV (még nem számlázott tételek) —
//    visszamenőleg az év elejéig és előre három hónapra;
//  • Számlák: a számla-lista (fül, kereső, lejárt-figyelmeztetés, tétel-panel);
//  • Szolgáltatások: szolgáltatásonként az időszakok idővonala.
// A nézet és a hónap az URL-ben él (linkelhető); az URL-t a `lib/search-params.ts` string-értelmezője adja.
// A fiók-scope a sidebar váltójából jön, és MINDEN számlázási kérésben ott van.
import { PORTAL_PERMISSIONS } from '@atlas/shared';
import {
  CalendarDaysIcon,
  DocumentTextIcon,
  Square3Stack3DIcon,
} from '@heroicons/react/24/outline';
import { createFileRoute } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';

import { PortalBillingInvoicesView } from '@/components/portal/portal-billing-invoices-view';
import { PortalBillingMonthView } from '@/components/portal/portal-billing-month-view';
import { PortalBillingServicesView } from '@/components/portal/portal-billing-services-view';
import { PortalPermissionGate } from '@/components/portal/portal-permission-gate';
import { TabStrip } from '@/components/ui/tab-strip';
import { usePortalHead } from '@/features/portal/portal-context';
import { useListFilters } from '@/hooks/use-list-filters';

const searchSchema = z.object({
  view: z.enum(['month', 'invoices', 'services']).optional(),
  /** A havi áttekintés hónapja (`YYYY-MM`); üres → a mostani hónap. */
  month: z.string().optional(),
  // a Számlák nézet saját szűrői (az URL-ben maradnak)
  tab: z.enum(['issued', 'paid', 'storno']).optional(),
  q: z.string().optional(),
});

export const Route = createFileRoute('/portal/invoices')({
  validateSearch: searchSchema,
  component: PortalBillingPage,
});

const VIEWS = ['month', 'invoices', 'services'] as const;
type View = (typeof VIEWS)[number];
const DEFAULTS = { view: 'month' as View, month: '' };

/** A lap a jog-kapun át: jog nélkül magyarázat áll a lap helyett (a fal a BFF-ben van). */
function PortalBillingPage(): JSX.Element {
  return (
    <PortalPermissionGate permission={PORTAL_PERMISSIONS.billingView}>
      <PortalBillingPageBody />
    </PortalPermissionGate>
  );
}

function PortalBillingPageBody(): JSX.Element {
  const { t } = useTranslation();
  const { filters, setFilters } = useListFilters(DEFAULTS);
  const view: View = VIEWS.find((v) => v === filters.view) ?? 'month';

  usePortalHead({ title: t('portal.billing.title'), subtitle: t('portal.billing.subtitle') }, [
    t('portal.billing.title'),
  ]);

  return (
    <div className="space-y-5">
      <TabStrip
        value={view}
        onChange={(v) => setFilters({ view: VIEWS.find((x) => x === v) ?? 'month' })}
        items={[
          { value: 'month', label: t('portal.billing.views.month'), icon: <CalendarDaysIcon /> },
          {
            value: 'services',
            label: t('portal.billing.views.services'),
            icon: <Square3Stack3DIcon />,
          },
          {
            value: 'invoices',
            label: t('portal.billing.views.invoices'),
            icon: <DocumentTextIcon />,
          },
        ]}
        aria-label={t('portal.billing.views.aria')}
        // mobilon ikon nélkül: a három felirat így EGY sorban fér el 375 px-en (ikonnal a „Számlák" új sorba tört)
        className="max-sm:[&_svg]:hidden"
      />
      {view === 'month' && (
        <PortalBillingMonthView
          month={filters.month}
          onMonthChange={(m) => setFilters({ month: m })}
        />
      )}
      {view === 'invoices' && <PortalBillingInvoicesView />}
      {view === 'services' && <PortalBillingServicesView />}
    </div>
  );
}
